import { safeRedirectPath } from './safeRedirectPath';

/**
 * Carrying the MCP OAuth consent return path through the MFA step
 * (#12062 fix round).
 *
 * LEXOMA makes MFA mandatory, so an MCP client's sign-in on lexomat.sk always
 * passes through `/mfa/verify` (or `/mfa/enroll`). Before this, those steps
 * always landed on `/` afterwards, and the pending
 * `/auth/mcp-consent?authorization_id=…` was lost — the MCP host was stranded.
 *
 * Deliberately narrow: ONLY the consent route is carried. Every other target
 * keeps the pre-existing bare `/mfa/verify` → `/` behaviour, and the value is
 * first passed through `safeRedirectPath` (the shared open-redirect guard),
 * so nothing off-origin can come out on either side of the MFA step.
 */

const MCP_CONSENT_PATH = '/auth/mcp-consent';

/** True for `/auth/mcp-consent` itself or with a query string — nothing else. */
export function isMcpConsentPath(path: string): boolean {
  return path === MCP_CONSENT_PATH || path.startsWith(`${MCP_CONSENT_PATH}?`);
}

/**
 * `{ redirect: <consent> }` when the (sanitized) target is the MCP consent
 * route, else `undefined` — the query a link inside the MFA flow carries so
 * the next step still knows where to return (and, #12062, that it is part
 * of an MCP connect and must render brand-neutral).
 */
export function mcpConsentQuery(
  target: string | null | undefined
): { redirect: string } | undefined {
  const safe = safeRedirectPath(target, '');
  return safe && isMcpConsentPath(safe) ? { redirect: safe } : undefined;
}

/**
 * The MFA step URL for a post-sign-in target: `/mfa/<step>?redirect=<consent>`
 * when the (sanitized) target is the MCP consent route, else the bare
 * `/mfa/<step>` exactly as before. `recover` is the lost-authenticator page.
 */
export function mfaStepPath(
  step: 'verify' | 'enroll' | 'recover',
  target: string | null | undefined
): string {
  const base = `/mfa/${step}`;
  const query = mcpConsentQuery(target);
  if (!query) return base;
  return `${base}?redirect=${encodeURIComponent(query.redirect)}`;
}

/**
 * Where an `/mfa/*` page sends the user once the session is aal2: the carried
 * consent path, or `/` for anything else (missing, external, smuggled, or
 * simply not the consent route). `?redirect=` on these pages is therefore
 * never an open redirect.
 */
export function mfaReturnTarget(raw: string | null | undefined): string {
  const safe = safeRedirectPath(raw, '');
  return safe && isMcpConsentPath(safe) ? safe : '/';
}

/**
 * How an `/mfa/*` page renders for its `?redirect=` (#12062): the return
 * target ({@link mfaReturnTarget}) and whether this is the MCP OAuth connect
 * flow. The flag is true exactly when the target is the allow-listed consent
 * route — then the page uses the brand-neutral shell, because a LEXOMA user
 * connecting an MCP client must not see lexomat.sk chrome. Anything else
 * (missing, external, smuggled, another path) is the ordinary lexomat.sk page
 * returning to `/`.
 */
export function mfaPageMode(raw: string | null | undefined): {
  returnTo: string;
  mcpAuthFlow: boolean;
} {
  const returnTo = mfaReturnTarget(raw);
  return { returnTo, mcpAuthFlow: isMcpConsentPath(returnTo) };
}
